import React, { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common';
import {
	useExperienceFail,
	useExperienceStart,
	useExperienceSuccess,
} from '@atlassian/jira-business-experience-tracking';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { useFlagService } from '@atlassian/jira-flags';
import { useFetchReleases } from '@atlassian/jira-shared-software-dates/src/controllers/use-fetch-releases';
import { JIRA_CORE, getEdition } from '@atlassian/jira-shared-types';
import { editionToEditionKey } from '@atlassian/jira-shared-types/src/edition';
import { useAppEditions, useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import { FETCH_RELEASE_EXPERIENCE } from '../../common/constants';
import { useGetConnectedProjects } from '../connected-projects-multi';
import { useHideSoftwareRelease } from '../hide-software-release';
import { loadSoftwareReleasesExperience } from './experiences';
import messages from './messages';
import {
	ERROR_ACTION,
	FETCHING_RELEASES_ACTION,
	PROJECTS_LOADED_ACTION,
	RELEASES_LOADED_ACTION,
	type ActionType,
	type CalendarReleasesInput,
	type CalendarReleasesInternalProps,
	type StateType,
} from './types';
import { transformData } from './utils';

export const defaultCalendarReleasesState: StateType = {
	releases: [],
	isLoading: true,
	error: null,
	connectedProjects: [],
};

const CalendarReleasesInternal = memo(
	({
		releaseDateBefore,
		releaseDateAfter,
		projectId,
		releasesDataDispatch,
	}: CalendarReleasesInternalProps) => {
		const { connectedProjects, error: initConnectedProjectError } =
			useGetConnectedProjects(projectId);
		const { showFlag } = useFlagService();

		const { fetchReleases } = useFetchReleases();

		// TODO WOLVES-561 remove these events once UFO-based "business_calendar_fetch_releases_reliability" chart has enough data
		const startExperience = useExperienceStart(FETCH_RELEASE_EXPERIENCE);
		const onSuccess = useExperienceSuccess(FETCH_RELEASE_EXPERIENCE);
		const onFail = useExperienceFail(FETCH_RELEASE_EXPERIENCE);

		const [hideSoftwareRelease] = useHideSoftwareRelease();
		useEffect(() => {
			startExperience();
			loadSoftwareReleasesExperience.start();
		}, [startExperience]);

		useEffect(() => {
			if (initConnectedProjectError) {
				const location = 'Did not fetch releases due to failing to fetch the connected project(s)';
				onFail(location, initConnectedProjectError);
				loadSoftwareReleasesExperience.failure({
					metadata: {
						location,
						error: initConnectedProjectError.message,
					},
				});
				showFlag({
					type: 'error',
					title: messages.fetchReleasesErrorTitle,
					description: messages.fetchReleasesErrorMessage,
				});
			}
		}, [initConnectedProjectError, onFail, showFlag]);

		useEffect(() => {
			async function fetchData() {
				// NOTE: By default connectedProjects is undefined, so we know loading has finished when it has a reference
				if (connectedProjects) {
					if (connectedProjects.length && !hideSoftwareRelease) {
						try {
							releasesDataDispatch({ type: FETCHING_RELEASES_ACTION, connectedProjects });

							const projectAris = connectedProjects.map(
								(connectedProject) => connectedProject.resourceId,
							);
							const releasesByProjects = await fetchReleases({
								projectAris,
								releaseDateBefore,
								releaseDateAfter,
							});

							releasesDataDispatch({
								type: RELEASES_LOADED_ACTION,
								releases: transformData(releasesByProjects ?? []),
							});

							onSuccess();
							loadSoftwareReleasesExperience.success();
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						} catch (err: any) {
							releasesDataDispatch({ type: ERROR_ACTION, error: err });
							showFlag({
								type: 'error',
								title: messages.fetchReleasesErrorTitle,
								description: messages.fetchReleasesErrorMessage,
							});
							onFail('Failed to fetch releases', err);
							loadSoftwareReleasesExperience.failure({
								metadata: {
									error: err?.message,
								},
							});
						}
					} else {
						releasesDataDispatch({ type: PROJECTS_LOADED_ACTION, connectedProjects });
					}
				}
			}

			fetchData();
		}, [
			connectedProjects,
			fetchReleases,
			hideSoftwareRelease,
			onFail,
			onSuccess,
			releaseDateAfter,
			releaseDateBefore,
			releasesDataDispatch,
			showFlag,
		]);

		return null;
	},
);

export const useCalendarReleasesForMultipleProjectsInitializer = (
	request: CalendarReleasesInput,
) => {
	const [calendarReleasesData, releasesDataDispatch] = useReducer(
		releasesDataReducer,
		defaultCalendarReleasesState,
	);
	const { data: entity } = useBusinessEntity();
	const { releaseDateAfter, releaseDateBefore } = request;
	const licensedProducts = useLicensedProducts();
	const hasSoftwareLicence = licensedProducts?.[LICENSED_PRODUCTS.JIRA_SOFTWARE];

	const appEditions = useAppEditions();
	const appEdition = editionToEditionKey(getEdition(JIRA_CORE, appEditions));

	const [hideSoftwareRelease] = useHideSoftwareRelease();

	const CalendarReleasesInitializer = useCallback(
		() =>
			hasSoftwareLicence ? (
				<CalendarReleasesInternal
					projectId={entity.id}
					releaseDateAfter={releaseDateAfter}
					releaseDateBefore={releaseDateBefore}
					releasesDataDispatch={releasesDataDispatch}
				/>
			) : null,
		[releaseDateAfter, releaseDateBefore, entity, hasSoftwareLicence],
	);

	const analyticsAttributes = useMemo(
		() => ({
			releasesCount: calendarReleasesData.releases.length ?? 0,
			connectedProjectsCount: calendarReleasesData.connectedProjects.length ?? 0,
			hasConnectedProject: calendarReleasesData.connectedProjects.length > 0,
			releasesToggleOn: !hideSoftwareRelease,
			edition: appEdition,
		}),
		[
			appEdition,
			calendarReleasesData.connectedProjects.length,
			calendarReleasesData.releases.length,
			hideSoftwareRelease,
		],
	);

	return {
		CalendarReleasesInitializer,
		calendarReleasesData,
		analyticsAttributes,
	};
};

const releasesDataReducer = (state: StateType, action: ActionType): StateType => {
	switch (action.type) {
		case PROJECTS_LOADED_ACTION: {
			return {
				releases: [],
				isLoading: false,
				error: null,
				connectedProjects: action.connectedProjects,
			};
		}

		case FETCHING_RELEASES_ACTION: {
			return {
				releases: [],
				isLoading: true,
				error: null,
				connectedProjects: action.connectedProjects,
			};
		}

		case RELEASES_LOADED_ACTION: {
			return { ...state, releases: action.releases, isLoading: false };
		}

		case ERROR_ACTION: {
			return { ...state, isLoading: false, error: action.error };
		}

		default:
			return state;
	}
};
